import type { FuseReport } from "./fuseReport";

export type FuseConsumerIcon = { data: string; width: number; height: number };
export type FuseConsumerIcons = Map<string, FuseConsumerIcon>;

/** Rasterizes an existing consumer icon at a bounded resolution; unavailable images use the normal connection marker. */
function loadConsumerIcon(source: string): Promise<FuseConsumerIcon | undefined> {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    const timeout = setTimeout(() => finish(undefined), 10000);
    const finish = (icon: FuseConsumerIcon | undefined) => {
      clearTimeout(timeout);
      image.onload = null;
      image.onerror = null;
      resolve(icon);
    };
    image.onerror = () => finish(undefined);
    image.onload = () => {
      try {
        if (!image.naturalWidth || !image.naturalHeight) {
          finish(undefined);
          return;
        }
        const scale = Math.min(1, 64 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext("2d");
        if (!context) {
          finish(undefined);
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        finish({ data: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height });
      } catch {
        finish(undefined);
      }
    };
    image.src = source;
  });
}

/** Loads each distinct icon once per export so repeated consumers reuse one PDF image resource. */
export async function loadFuseConsumerIcons(report: FuseReport): Promise<FuseConsumerIcons> {
  const sources = new Set<string>();
  for (const section of report.sections)
    for (const circuit of section.circuits)
      for (const consumer of circuit.consumers)
        for (const node of consumer.route) if (node.kind === "consumer" && node.icon) sources.add(node.icon);
  const icons: FuseConsumerIcons = new Map();
  await Promise.all(
    Array.from(sources, async (source) => {
      const icon = await loadConsumerIcon(source);
      if (icon) icons.set(source, icon);
    }),
  );
  return icons;
}
